import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Zap, ShieldCheck, Lightbulb, Lock, Handshake, Award, ArrowRight, Globe } from 'lucide-react';
import Navbar from './landing/Navbar';
import Footer from './landing/Footer';
import { Container, SectionHeading, GlassCard, IconBadge, fadeInUp } from './landing/shared';

const MISSION_PILLARS = [
  { icon: Zap, key: 'innovation' },
  { icon: ShieldCheck, key: 'reliability' },
  { icon: Handshake, key: 'partnership' },
];

const VALUES = [
  { icon: Lightbulb, key: 'innovation' },
  { icon: Lock, key: 'security' },
  { icon: Handshake, key: 'collaboration' },
  { icon: Award, key: 'excellence' },
];

const AboutUs: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen overflow-hidden bg-brand-gradient text-white">
      <Navbar />

      <main className="pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden pb-20 pt-16 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute left-1/2 top-[-10%] h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-brand-primary/20 blur-[140px]" />
            <div className="absolute right-[-10%] top-[20%] h-[350px] w-[450px] rounded-full bg-brand-secondary/10 blur-[120px]" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]" />
          </div>

          <Container className="relative text-center">
            <motion.span
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-secondary backdrop-blur-xl"
            >
              {t('about.home')} · {t('about.title')}
            </motion.span>

            <motion.h1
              variants={fadeInUp}
              custom={1}
              initial="hidden"
              animate="visible"
              className="mx-auto max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              {t('about.title')}
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              custom={2}
              initial="hidden"
              animate="visible"
              className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-brand-muted sm:text-xl"
            >
              {t('about.subtitle')}
            </motion.p>
          </Container>
        </section>

        {/* Mission */}
        <section className="relative py-24 sm:py-32">
          <Container>
            <div className="grid gap-16 lg:grid-cols-2 lg:items-center lg:gap-12">
              <div>
                <SectionHeading
                  align="left"
                  eyebrow={t('about.mission.title')}
                  title={t('about.mission.title')}
                />
                <motion.p
                  variants={fadeInUp}
                  custom={1}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-6 text-lg leading-relaxed text-brand-muted"
                >
                  {t('about.mission.paragraph1')}
                </motion.p>
                <motion.p
                  variants={fadeInUp}
                  custom={2}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-4 leading-relaxed text-brand-muted"
                >
                  {t('about.mission.paragraph2')}
                </motion.p>
                <motion.p
                  variants={fadeInUp}
                  custom={3}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  className="mt-4 leading-relaxed text-brand-muted"
                >
                  {t('about.mission.paragraph3')}
                </motion.p>
              </div>

              <div className="grid gap-4">
                {MISSION_PILLARS.map(({ icon: Icon, key }, i) => (
                  <GlassCard key={key} index={i} className="flex items-center gap-5 p-6">
                    <IconBadge>
                      <Icon className="h-6 w-6 text-white" />
                    </IconBadge>
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {t(`about.mission.${key}.title`)}
                      </h3>
                      <p className="mt-1 text-sm text-brand-muted">
                        {t(`about.mission.${key}.description`)}
                      </p>
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Team */}
        <section className="relative py-24 sm:py-32">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute left-[-10%] top-[40%] h-[400px] w-[500px] rounded-full bg-brand-primary/10 blur-[140px]" />
          </div>

          <Container className="relative">
            <SectionHeading
              eyebrow={t('about.team.title')}
              title={t('about.team.title')}
              subtitle={t('about.team.subtitle')}
            />

            <div className="mt-16 grid gap-6 lg:grid-cols-3">
              {/* Aleksandra */}
              <GlassCard index={0} className="text-center">
                <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-500 shadow-glow">
                  <span className="text-xl font-bold text-white">AK</span>
                </div>
                <h3 className="text-xl font-semibold text-white">{t('about.team.aleksandraName')}</h3>
                <p className="mt-1 text-sm font-semibold text-brand-secondary">{t('about.team.aleksandraRole')}</p>
                <p className="mt-4 leading-relaxed text-brand-muted">{t('about.team.aleksandraDescription')}</p>
              </GlassCard>

              {/* Lukasz */}
              <GlassCard index={1} className="text-center">
                <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary shadow-glow">
                  <span className="text-xl font-bold text-white">LP</span>
                </div>
                <h3 className="text-xl font-semibold text-white">{t('about.team.lukaszName')}</h3>
                <p className="mt-1 text-sm font-semibold text-brand-secondary">{t('about.team.lukaszRole')}</p>
                <p className="mt-4 leading-relaxed text-brand-muted">{t('about.team.lukaszDescription')}</p>
              </GlassCard>

              {/* Global Team */}
              <GlassCard index={2} className="text-center">
                <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 shadow-glow">
                  <Globe className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white">{t('about.team.nisargName')}</h3>
                <p className="mt-1 text-sm font-semibold text-brand-secondary">{t('about.team.nisargRole')}</p>
                <p className="mt-4 leading-relaxed text-brand-muted">{t('about.team.nisargDescription')}</p>
              </GlassCard>
            </div>
          </Container>
        </section>

        {/* Values */}
        <section className="relative py-24 sm:py-32">
          <Container>
            <SectionHeading eyebrow={t('about.values.title')} title={t('about.values.title')} />

            <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {VALUES.map(({ icon: Icon, key }, i) => (
                <GlassCard key={key} index={i} className="text-center">
                  <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-button shadow-glow">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="mb-3 text-lg font-semibold text-white">{t(`about.values.${key}.title`)}</h3>
                  <p className="text-sm leading-relaxed text-brand-muted">{t(`about.values.${key}.description`)}</p>
                </GlassCard>
              ))}
            </div>
          </Container>
        </section>

        {/* CTA */}
        <section className="relative py-24 sm:py-32">
          <Container>
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
              className="glass-card relative overflow-hidden rounded-3xl px-8 py-16 text-center sm:px-16"
            >
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute left-1/2 top-0 h-[300px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-primary/25 blur-[120px]" />
              </div>

              <div className="relative">
                <h2 className="mx-auto max-w-3xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {t('about.cta.title')}
                </h2>
                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-brand-muted">
                  {t('about.cta.subtitle')}
                </p>

                <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <button
                    onClick={() => navigate('/demo')}
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-button px-8 py-4 text-base font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.03]"
                  >
                    {t('about.cta.getInTouch')}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </button>
                  <Link
                    to="/careers"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-8 py-4 text-base font-semibold text-white backdrop-blur-xl transition-colors duration-300 hover:bg-white/[0.08]"
                  >
                    {t('about.cta.joinTeam')}
                  </Link>
                </div>
              </div>
            </motion.div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
